/**
 * Module 00 — Global Shell · Route adapter (FE-00-03)
 *
 * Bridges the prototype's `{ route, go }` contract onto the Next App Router.
 * The active `route` is the first path segment (default `home`); `go(route)`
 * pushes `/<route>` and remembers it as the last advanced route (SM-02). The
 * `NAV` `route` strings are kept identical to path segments so deep-links and
 * the command palette keep working.
 */

'use client';

import { useCallback } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useUiStore } from '@/lib/stores/ui-store';

/**
 * Maps a URL path segment tuple to the NAV `route` key.
 * Kept in sync with ROUTE_TO_PATH below — every route here must have a
 * reverse entry there so go() pushes the correct URL.
 */
export function routeFromPath(pathname: string | null | undefined): string {
  if (!pathname) return 'home';
  const segs = pathname.split('/').filter(Boolean);
  if (!segs.length) return 'home';

  const [s0, s1, s2] = segs;

  // Module 01 — Workspace: /do-mode → nav key 'do', /history → nav key 'history'
  if (s0 === 'do-mode') return 'do';

  // Module 02 — Connect: /connect/connectors, /connect/tools
  // (AI Models moved to /settings/org/ai-models — no longer a Connect route)
  if (s0 === 'connect' && s1) return `connect/${s1}`;

  // Module 03 — Orchestrate: /orchestrate/<sub>
  if (s0 === 'orchestrate') {
    if (s1 === 'skills' && s2 === 'marketplace') return 'skills-marketplace';
    if (s1 === 'skills' && s2 === 'approvals') return 'skills-approvals';
    if (s1 === 'skills' && s2 === 'learn') return 'learn';
    if (s1) return s1; // 'agents' | 'process-flows' | 'executions' | 'skills'
  }

  // Module 04 — Context Graph: /context-graph/<sub>
  if (s0 === 'context-graph' && s1) return `context-graph/${s1}`;

  // /projects/{id}/tables (list) and /projects/{id}/tables/{tableId} (detail)
  // both belong to the Context Graph › Tables module — keep the nav item highlighted.
  if (s0 === 'projects' && s2 === 'tables') return 'context-graph/tables';

  // Module 05 — Actions: /actions/<sub>
  if (s0 === 'actions' && s1) return `actions/${s1}`;

  // Module 06 — Simulate: /simulate/<sub>
  if (s0 === 'simulate' && s1) return `simulate/${s1}`;

  // Module 07 — Data Management: /data-management/<sub>
  if (s0 === 'data-management' && s1) return `data-management/${s1}`;

  // Module 08 — Governance: /governance/<sub>
  if (s0 === 'governance' && s1) return `governance/${s1}`;

  // analytics-service workflow studio: /analytics/* → nav key 'analytics-studio'
  if (s0 === 'analytics') return 'analytics-studio';

  // App marketplace: /apps/installed and an install's own page
  // (/apps/installs/<id>) highlight "Installed apps"; the catalogue, an app's
  // detail page and its install wizard highlight "Marketplace". `installs` and
  // `installed` are reserved app ids, so no app's own page can collide with them.
  if (s0 === 'apps') {
    if (s1 === 'installed' || s1 === 'installs') return 'apps/installed';
    return 'apps';
  }

  // Module 10 — Settings: /settings/<sub> → `settings/org`, `settings/users`, …
  if (s0 === 'settings' && s1) return `${s0}/${s1}`;

  return s0 || 'home';
}

/**
 * Maps NAV `route` keys to their canonical URL paths.
 * When no entry exists the route key is used verbatim as the path segment.
 */
const ROUTE_TO_PATH: Record<string, string> = {
  // Module 00 — Workspace
  home: '/',
  do: '/do-mode',
  history: '/history',

  // Module 02 — Connect (already 1:1 with path)
  'connect/mcp-servers': '/connect/mcp-servers',

  // Module 03 — Orchestrate (nested under /orchestrate/)
  agents: '/orchestrate/agents',
  'process-flows': '/orchestrate/process-flows',
  executions: '/orchestrate/executions',
  skills: '/orchestrate/skills',
  'skills-marketplace': '/orchestrate/skills/marketplace',
  'skills-approvals': '/orchestrate/skills/approvals',
  learn: '/orchestrate/skills/learn',

  // Module 05 — Actions (nested under /actions/)
  'actions/action-registry': '/actions/action-registry',
  'actions/cross-system-mappings': '/actions/cross-system-mappings',
  'actions/action-simulation': '/actions/action-simulation',
  'actions/approval-flows': '/actions/approval-flows',

  // Module 06 — Simulate (nested under /simulate/)
  'simulate/agent': '/simulate/agent',
  'simulate/connector': '/simulate/connector',

  // Module 09 — Insights
  'analytics-studio': '/analytics',

  // Module 07 — Data Management
  'data-management/ingestion': '/data-management/ingestion',
  'data-management/mappings': '/data-management/mappings',

  // Module 08 — Governance (already 1:1)
  'governance/guardrails': '/governance/guardrails',
  'governance/responsible-ai': '/governance/responsible-ai',
  'governance/audit': '/governance/audit',
  'governance/compliance': '/governance/compliance',

  // Module 10 — Settings
  'settings/org': '/settings/org',
  'settings/users': '/settings/users',
  'settings/access-grants': '/settings/access-grants',
  'settings/departments': '/settings/departments',
  'settings/projects': '/settings/projects',
  'settings/policy': '/settings/policy',
  'settings/constitution': '/settings/constitution',
  'settings/usage': '/settings/usage',
  'settings/developer-access': '/settings/developer-access',
  'settings/billing': '/settings/billing',
};

/**
 * Resolve a NAV `route` key to its canonical URL path. Exported so nav items can
 * prefetch the target route (on hover) without re-implementing the mapping.
 */
export function pathForRoute(route: string): string {
  return ROUTE_TO_PATH[route] ?? `/${route}`;
}

export function useShellNav(): { route: string; go: (route: string) => void } {
  const pathname = usePathname();
  const router = useRouter();
  const route = routeFromPath(pathname);
  const setLastAdvancedRoute = useUiStore((s) => s.setLastAdvancedRoute);

  const go = useCallback(
    (next: string) => {
      setLastAdvancedRoute(next);
      const path = pathForRoute(next);
      router.push(path);
      if (typeof document !== 'undefined') {
        const page = document.querySelector('[data-shell-scroll]');
        if (page) page.scrollTop = 0;
      }
    },
    [router, setLastAdvancedRoute],
  );

  return { route, go };
}
